import { useEffect, useState, type KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { Lock, Unlock, X } from 'lucide-react';
import type { AudienceState } from './WriterPage';
import { AUDIENCES } from '@starfox/shared-types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  episodeId: string;
  audience: AudienceState;
  onSaved: () => void;
}

interface AudienceLockBody {
  primary: string;
  secondary: string[];
  ageRange: [number, number] | null;
  gender: 'all' | 'female' | 'male';
  regions: string[];
  tags: string[];
  locked: boolean;
}

const SECONDARY_PRESETS = ['泛 Z 世代', '都市白领', '银发用户', '学生群体', '宝妈人群', '小镇青年'];
const REGION_OPTIONS = ['一线城市', '新一线', '二三线', '下沉市场', '海外华人'];
const GENDER_OPTIONS: { label: string; value: 'all' | 'female' | 'male' }[] = [
  { label: '不限', value: 'all' },
  { label: '女性向', value: 'female' },
  { label: '男性向', value: 'male' },
];

export function AudienceLockDialog({ open, onOpenChange, episodeId, audience, onSaved }: Props) {
  const qc = useQueryClient();
  const [primary, setPrimary] = useState<string>(audience.primary ?? AUDIENCES[0]);
  const [secondaryDraft, setSecondaryDraft] = useState('');
  const [secondary, setSecondary] = useState<string[]>(audience.secondary ?? []);
  const [ageMin, setAgeMin] = useState<number>(audience.ageRange?.[0] ?? 18);
  const [ageMax, setAgeMax] = useState<number>(audience.ageRange?.[1] ?? 35);
  const [gender, setGender] = useState<'all' | 'female' | 'male'>(audience.gender ?? 'all');
  const [regions, setRegions] = useState<string[]>(audience.regions ?? []);
  const [tagDraft, setTagDraft] = useState('');
  const [tags, setTags] = useState<string[]>(audience.tags ?? []);

  useEffect(() => {
    if (!open) return;
    setPrimary(audience.primary ?? AUDIENCES[0]);
    setSecondary(audience.secondary ?? []);
    setAgeMin(audience.ageRange?.[0] ?? 18);
    setAgeMax(audience.ageRange?.[1] ?? 35);
    setGender(audience.gender ?? 'all');
    setRegions(audience.regions ?? []);
    setTags(audience.tags ?? []);
    setSecondaryDraft('');
    setTagDraft('');
  }, [open, audience]);

  const mut = useMutation({
    mutationFn: async (body: AudienceLockBody) => {
      return api
        .post(`writer/episodes/${episodeId}/audience-lock`, { json: body })
        .json<{ audience: Record<string, unknown> }>();
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['episode', episodeId] });
      qc.invalidateQueries({ queryKey: ['atlas', episodeId] });
      toast.success(vars.locked ? '受众已锁定' : '已解除受众锁定');
      onSaved();
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error('保存失败', err.message),
  });

  const submit = (lock: boolean) => {
    if (lock && !primary.trim()) {
      toast.warning('请选择或填写主要人群');
      return;
    }
    const lo = Math.min(ageMin, ageMax);
    const hi = Math.max(ageMin, ageMax);
    mut.mutate({
      primary: primary.trim(),
      secondary,
      ageRange: lock ? [lo, hi] : null,
      gender,
      regions,
      tags,
      locked: lock,
    });
  };

  const toggleRegion = (r: string) =>
    setRegions((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  const pushTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    if (!tags.includes(t)) setTags((cur) => [...cur, t]);
    setTagDraft('');
  };

  const pushSecondary = () => {
    const t = secondaryDraft.trim();
    if (!t) return;
    if (!secondary.includes(t)) setSecondary((cur) => [...cur, t]);
    setSecondaryDraft('');
  };

  const onTagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      pushTag();
    }
  };
  const onSecondaryKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      pushSecondary();
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="目标用户画像 · Audience Lock"
      description="锁定后,后续 Path / Atlas 内容会按此目标人群差异化"
      className="max-w-lg"
      footer={
        <div className="flex w-full flex-wrap justify-between gap-2">
          {audience.locked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => submit(false)}
              disabled={mut.isPending}
            >
              <Unlock className="h-4 w-4" /> 解除锁定
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button
              variant="agent"
              agentColor="writer"
              size="sm"
              onClick={() => submit(true)}
              disabled={mut.isPending}
            >
              <Lock className="h-4 w-4" /> {audience.locked ? '更新画像' : '锁定画像'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <Field label="人群频道">
          <div className="flex flex-wrap gap-1.5">
            {AUDIENCES.map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => setPrimary(o)}
                className={
                  primary === o
                    ? 'rounded-full bg-brand-500 px-3 py-1 text-xs text-white shadow-sm'
                    : 'rounded-full border border-outline-variant/40 bg-surface px-3 py-1 text-xs text-neutral-700 transition hover:border-brand-400'
                }
              >
                {o}
              </button>
            ))}
          </div>
        </Field>

        <Field label="细分受众（可多选）">
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {SECONDARY_PRESETS.map((o) => {
              const active = secondary.includes(o);
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() =>
                    setSecondary((cur) =>
                      active ? cur.filter((x) => x !== o) : [...cur, o],
                    )
                  }
                  className={
                    active
                      ? 'rounded-full bg-brand-500 px-3 py-1 text-xs text-white shadow-sm'
                      : 'rounded-full border border-outline-variant/40 bg-surface px-3 py-1 text-xs text-neutral-700 transition hover:border-brand-400'
                  }
                >
                  {o}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {secondary.filter((s) => !SECONDARY_PRESETS.includes(s)).map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs text-brand-700"
              >
                {s}
                <button
                  type="button"
                  onClick={() => setSecondary((cur) => cur.filter((x) => x !== s))}
                  className="rounded-full text-brand-500 transition hover:text-brand-700"
                  aria-label="移除"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <Input
              value={secondaryDraft}
              onChange={(e) => setSecondaryDraft(e.target.value)}
              onKeyDown={onSecondaryKey}
              onBlur={pushSecondary}
              placeholder="回车自定义添加"
              className="h-8 w-40 flex-1 text-xs"
            />
          </div>
        </Field>

        <Field label={`年龄区间 · ${Math.min(ageMin, ageMax)}-${Math.max(ageMin, ageMax)} 岁`}>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="text-xs text-outline">最低</span>
              <Input
                type="number"
                min={6}
                max={80}
                value={ageMin}
                onChange={(e) => setAgeMin(Number(e.target.value) || 0)}
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-outline">最高</span>
              <Input
                type="number"
                min={6}
                max={80}
                value={ageMax}
                onChange={(e) => setAgeMax(Number(e.target.value) || 0)}
                className="h-9 text-xs"
              />
            </div>
          </div>
        </Field>

        <Field label="性别倾向">
          <div className="flex flex-wrap gap-1.5">
            {GENDER_OPTIONS.map((g) => (
              <button
                key={g.value}
                type="button"
                onClick={() => setGender(g.value)}
                className={
                  gender === g.value
                    ? 'rounded-full bg-brand-500 px-3 py-1 text-xs text-white shadow-sm'
                    : 'rounded-full border border-outline-variant/40 bg-surface px-3 py-1 text-xs text-neutral-700 transition hover:border-brand-400'
                }
              >
                {g.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="地域标签(可多选)">
          <div className="flex flex-wrap gap-1.5">
            {REGION_OPTIONS.map((r) => {
              const active = regions.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleRegion(r)}
                  className={
                    active
                      ? 'rounded-full bg-brand-500 px-3 py-1 text-xs text-white shadow-sm'
                      : 'rounded-full border border-outline-variant/40 bg-surface px-3 py-1 text-xs text-neutral-700 transition hover:border-brand-400'
                  }
                >
                  {r}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="兴趣 / 关键词">
          <div className="flex flex-wrap items-center gap-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-full bg-warning-soft/60 px-2.5 py-1 text-xs text-warning"
              >
                {t}
                <button
                  type="button"
                  onClick={() => setTags((cur) => cur.filter((x) => x !== t))}
                  className="rounded-full text-warning transition hover:opacity-70"
                  aria-label="移除"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <Input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={onTagKey}
              onBlur={pushTag}
              placeholder="回车 / 逗号添加"
              className="h-8 w-40 flex-1 text-xs"
            />
          </div>
        </Field>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs uppercase tracking-wider text-outline">{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
